import {AfterViewInit, Component, OnInit} from '@angular/core';
import {StepService} from '../services/step.service';
import {Router} from '@angular/router';
import {ElementService} from '../services/element.service';
import {PortalService} from '../services/portal.service';
import {EnergyMapService} from './services/energy.map.service';
import {EnergyMapOverlayComponent} from './energy.map.overlay.component';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {EnergyMapConstants} from './energy.map.constants';
import {EnergyMapAddressComponent} from './energy.map.address.component';

/**
 * Special component for the step energy-map if a Berlin ZIP code was entered at the beginning of the advisor.
 */
@Component({
    selector: 'app-energy-map',
    templateUrl: './energy.map.component.html',
    standalone: false
})
export class EnergyMapComponent implements OnInit, AfterViewInit {
  /**
   * Advisor name
   */
  advisorName: string = '';

  /**
   * prefix for some text keys. Can be used to indicate the advisors or others.
   */
  textkeyPrefix: string = '';

  /**
   * The last step were we should navigate with the back button to.
   */
  lastStep: string = null;

  /**
   * The next step were we should navigate with the next button to.
   */
  nextStep: string = null;

  /**
   * The next step when users have successfully donated.
   */
  nextStepDonated: string = null;

  /**
   * Requestservice to talk with the backend
   * @protected
   */
  protected requestService: any;

  /**
   * the anfrage object with the input of the user
   */
  anfrage: any;

  /**
   * error text key
   */
  error: string = null;

  /**
   * Placeholders for error text
   */
  errorPlaceholders: any = {};

  /**
   * selected buildings
   */
  selectedBuildings = [];

  /**
   * Energy map overlay dialog
   */
  energyMapOverlay: MatDialogRef<EnergyMapOverlayComponent> = null;

  /**
   * Energy map address overlay dialog
   */
  energyMapAddress: MatDialogRef<EnergyMapAddressComponent> = null;

  /**
   * When entering the step, a map with the center of the postcode is loaded.
   */
  buildingSelectionUrl = EnergyMapConstants.berlinMapBaseUrl + '/building-selection/postcode/{postcode}';

  /**
   * The map is blocked until users have entered their address details and clicked on the "In Karte reinzoomen" button.
   */
  isMapBlocked = true;

  /**
   *
   * @param stepService
   * @param router
   * @param elementService
   * @param portalService
   * @param energyMapService
   * @param dialog
   */
  constructor(protected stepService: StepService, protected router: Router, protected elementService: ElementService,
              protected portalService: PortalService, protected energyMapService: EnergyMapService, public dialog: MatDialog) {
  }

  /**
   * @inheritDoc
   */
  ngOnInit() {
    const plz = this.energyMapService.getPostleitzahl(this.anfrage, this.advisorName);
    this.buildingSelectionUrl = this.buildingSelectionUrl.replace('{postcode}', plz);
    // listen for message from iframe
    window.addEventListener('message', event => this.listen(event));

    // show the overlay with a delay
    setTimeout(() => this.showEnergyMapOverlay(), 800);
  }

  /**
   * @inheritDoc
   */
  ngAfterViewInit(): void {
    // scroll to the top
    const element = document.querySelector('body');
    this.elementService.scrollToElement(element);
  }

  /**
   * Listens for message from the iframe
   * @param event
   */
  listen(event: MessageEvent): void {
    // console.log('Received message from iframe', event);
    if (event.data && event.data.hasOwnProperty('uuid')) {
      if (this.isBuildigsSelected(event.data.uuid)) {
        this.removeBuilding(event.data.uuid);
      }
      else {
        this.addBuilding(event.data);
      }
    }
  }

  isBuildigsSelected(uuid: string) {
    let result = false;
    for (const building of this.selectedBuildings) {
      if (building.uuid === uuid) {
        result = true;
        break;
      }
    }
    return result;
  }

  addBuilding(data: any) {
    this.selectedBuildings.push(data);
  }

  removeBuilding(uuid: string) {
    let index = -1;
    for (let i = 0; i < this.selectedBuildings.length; i++) {
      if (this.selectedBuildings[i].uuid === uuid) {
        index = i;
        break;
      }
    }
    if (index >= 0) {
      this.selectedBuildings.splice(index, 1);
    }
  }

  submitClicked(event: { preventDefault: () => void; }) {
    if (this.selectedBuildings.length <= 0) {
      event.preventDefault();
      return false;
    }
    this.saveData({energy_map_data_donated: this.selectedBuildings});

    // Donate data
    const input = {
      energy_map_data_donated: this.selectedBuildings,
      gebaeude_stockwerke: this.energyMapService.getGebaeudeStockwerke(this.anfrage, this.advisorName)
    };
    this.requestService.backendService.getRequestId().subscribe(requestId => {
      this.energyMapService.datadonation(input, requestId);
    });
    return true;
  }

  buildingNotIncludedClicked() {
    this.saveData({energy_map_building_not_included_in_map: true});
  }

  /**
   * shows/opens the energy map overlay for the page
   */
  showEnergyMapOverlay(): void {
    // build the config for the overlay
    const overlayConfig = {
      disableClose: true,
      width: '880px',
      panelClass: 'energy-map-overlay',
      data: {
        textkeyPrefix: this.textkeyPrefix,
        lastStep: this.lastStep,
        nextStep: this.nextStep,
        anfrage: this.anfrage
      }
    };

    // open the dialog
    this.energyMapOverlay = this.dialog.open(EnergyMapOverlayComponent, overlayConfig);

    // handle closing of the dialog / button was clicked
    this.energyMapOverlay.afterClosed().subscribe(result => {
      if (result.data) {
        this.saveData(result.data);

        // no donation, forward to the next step
        if (this.anfrage.verbrauchsanalyseInput.energy_map_confirm_donation === 'n') {
          this.router.navigateByUrl(this.nextStep);
        }
        else {
          this.showEnergyMapAddress();
        }
      }
    });
  }

  /**
   * shows/opens the energy map address overlay for the page
   */
  showEnergyMapAddress(): void {
    // build the config for the overlay
    const overlayConfig = {
      disableClose: true,
      width: '880px',
      panelClass: 'energy-map-address',
      data: {
        textkeyPrefix: this.textkeyPrefix,
        lastStep: this.lastStep,
        nextStep: this.nextStep,
        anfrage: this.anfrage,
        advisorName: this.advisorName
      }
    };

    // open the dialog
    this.energyMapAddress = this.dialog.open(EnergyMapAddressComponent, overlayConfig);

    // handle closing of the dialog / button was clicked
    this.energyMapAddress.afterClosed().subscribe(result => {
      if (result === 'back') {
        this.showEnergyMapOverlay();
      }
    });
  }

  /**
   * Save the data.
   * @param data
   */
  saveData(data: {[key: string]: any}) {
    // merge the data with the existing data
    this.anfrage.verbrauchsanalyseInput = {...this.anfrage.verbrauchsanalyseInput, ...data};
    this.requestService.setAnfrage(this.anfrage);

    // save the data also to the server
    this.requestService.saveDataToServer();
  }
}
